import { useContext, useEffect, useRef, useState } from 'react';
import { IWatrReimbursement, IWatrRequest } from '@nkr/models/index';
import { WageAndTravelStatus } from '@nkr/enums/index';
import { DonorShieldContext } from '../context';
import { Button, Modal, Glyphicon } from 'react-bootstrap';
import './reimbursement-verify.css';
import WatrInfoAccordions from '../accordion';
import Header from '../header';
import VerificationStepTracker from '../verification-step-tracker';
import WageSummaryShared from '../common/wage-summary-shared';
import {
    WEEKLY_CAP,
    calculateAdjustedBaseAmount,
    computeWageModel,
    filterEligibleWeeks,
    getDefaultWeeklyWageBase,
    hasAnyWageData
} from '../common/wage-computation';
import { parseMoneyInput } from '../common/money-input';

interface IProps {
    data: IWatrRequest,
    isAudit: boolean,
    reimbursements: IWatrReimbursement[],
}

const VerifyAmount = (props: IProps) => {
    const [wageReimbursementAmount, setWageReimbursementAmount] = useState(0);
    const [editingWageReimbursementAmount, setEditingWageReimbursementAmount] = useState(false);
    const [sendEmail, setSendEmail] = useState(false);
    const [confirm, setConfirm] = useState<{ show: boolean, status: WageAndTravelStatus, requireEmail: boolean }>({ show: false, status: null, requireEmail: false });
    const [isConfirming, setIsConfirming] = useState(false);
    const [draftOtherAmounts, setDraftOtherAmounts] = useState<Record<number, number>>({});

    const prevRequestIdRef = useRef<number | null>(null);
    const context = useContext(DonorShieldContext);

    const calculateIncomePercent = (x: number) => {
        let percentToCover = 100 - props.data.incomePercentCovered;
        return Math.round((x * (percentToCover / 100)));
    };

    const finalWeeklyCalc = getDefaultWeeklyWageBase(props.data);
    const filteredWeeks = filterEligibleWeeks(props.data);
    const wageModel = computeWageModel(props.data, wageReimbursementAmount, draftOtherAmounts);
    const hasWageData = hasAnyWageData(props.data, wageReimbursementAmount);

    useEffect(() => {
        const requestId = props.data?.id;

        if (requestId === undefined || requestId === null) return;
        if (prevRequestIdRef.current === requestId) return;
        prevRequestIdRef.current = requestId;

        let amount = 0;
        
        if (props.data.wageReimbursementAmount > 0) {
            amount = calculateIncomePercent(props.data.wageReimbursementAmount);
        } else {
            const rawBaseAmount = finalWeeklyCalc || 0;
            const cappedBaseAmount = Math.min(rawBaseAmount, WEEKLY_CAP);
            const roundedBaseAmount = Math.round(cappedBaseAmount * 100) / 100;
            const incomePercentCovered = props.data.incomePercentCovered || 0;
            const adjustedBaseAmount = calculateAdjustedBaseAmount(roundedBaseAmount, incomePercentCovered, WEEKLY_CAP);
            
            if (adjustedBaseAmount > 0) {
                amount = Math.round(adjustedBaseAmount);
            } else if (props.data.actualWageReimbursementAmount > 0) {
                amount = props.data.incomePercentCovered <= 0
                    ? props.data.actualWageReimbursementAmount
                    : calculateIncomePercent(props.data.actualWageReimbursementAmount);
            } else if (props.data.estimatedWageReimbursementAmount > 0) {
                amount = calculateIncomePercent(props.data.estimatedWageReimbursementAmount);
            }
        }

        setWageReimbursementAmount(amount);
        setEditingWageReimbursementAmount(false);
        setSendEmail(true);
    }, [props.data?.id]);

    const handleFocus = (e: React.FocusEvent<any>) => {
        setEditingWageReimbursementAmount(true);
        // Selecting existing value avoids leading-zero prefix behavior while typing overrides.
        (e.currentTarget as HTMLInputElement).select();
    };

    const handleBlur = (_e: React.FocusEvent<any>) => {
        setEditingWageReimbursementAmount(false);
    };

    const handleWageReimbursementAmountChange = (e: React.FormEvent<any>) => {
        const val = (e.currentTarget as HTMLInputElement).value;
        const parsed = parseMoneyInput(val);
        const amount = Math.max(0, Math.round(parsed * 100) / 100);
        setWageReimbursementAmount(amount);
    };

    const handleVerifyAmount = (status: WageAndTravelStatus, requireEmail: boolean) => {
        setConfirm({ show: true, status, requireEmail });
    };

    const persistDraftOtherAmounts = async () => {
        if (!context.onUpdateRequestWeekOtherAmount) return;
        const updates = filteredWeeks.filter(w => {
            if ((w.reimbursementId || 0) > 0) return false;
            const newAmount = draftOtherAmounts[w.weekRequested] ?? w.otherAmount ?? 0;
            const currentAmount = w.otherAmount ?? 0;
            return newAmount !== currentAmount;
        });
        await Promise.all(updates.map(w =>
            context.onUpdateRequestWeekOtherAmount(props.data.id, w.weekRequested, draftOtherAmounts[w.weekRequested] ?? w.otherAmount ?? 0)
        ));
    };

    const handleConfirm = async () => {
        const status = confirm.status;
        const requireEmail = confirm.requireEmail;
        setIsConfirming(true);
        setConfirm({ show: false, status: null, requireEmail: false });
        try {
            if (context.showLoading) {
                await context.showLoading('Verifying amount...');
            }
            await persistDraftOtherAmounts();
            context.onVerifyAmount(props.data.id, status, sendEmail, wageReimbursementAmount, requireEmail);
        } finally {
            setIsConfirming(false);
        }
    };

    const handleCancelConfirm = () => {
        if (isConfirming) return;
        setConfirm({ show: false, status: null, requireEmail: false });
    };

    const handleToggleSendEmail = () => {
        setSendEmail(!sendEmail);
    };

    const handleDeclineAmount = async () => {
        await persistDraftOtherAmounts();
        context.onShowDeclineModal(props.data.id, false);
    };

    return (
        <>
            <Header data={props.data} showStatus={false} />
            <VerificationStepTracker data={props.data} />
            <div className="reimbursement-verify">
                <div className="page-wrap">
                    <form>
                        <WageSummaryShared
                            data={props.data}
                            mode="verify"
                            wageReimbursementAmount={wageReimbursementAmount}
                            draftOtherAmounts={draftOtherAmounts}
                            onOtherAmountChange={(weekRequested, otherAmount) => setDraftOtherAmounts(prev => ({ ...prev, [weekRequested]: otherAmount }))}
                            onOverrideFocus={handleFocus}
                            onOverrideBlur={handleBlur}
                            onOverrideChange={handleWageReimbursementAmountChange}
                        />

                        {hasWageData && (
                            <>
                                <div className="panel" style={{ marginBottom: 0 }}>
                                    <div className="abar abar-summary" style={{ borderTop: 'none' }}>
                                        <span className="note">Final reimbursement: <strong>${wageModel.finalReimbursementTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></span>
                                        <Button onClick={() => handleVerifyAmount(WageAndTravelStatus.Donated, false)}>
                                            Convert to Travel Only
                                        </Button>
                                    </div>
                                </div>

                                <div className="verify-amount-actions">
                                    <Button bsStyle="primary" className="btn-danger" onClick={handleDeclineAmount}>
                                        Decline
                                    </Button>
                                    <Button
                                        bsStyle="success"
                                        onClick={() => handleVerifyAmount(WageAndTravelStatus.PendingWageReimbursement, false)}
                                    >
                                        Approve
                                    </Button>
                                </div>
                            </>
                        )}
                    </form>
                </div>
            </div>

            <WatrInfoAccordions
                data={props.data}
                isAudit={props.isAudit}
                reimbursements={props.reimbursements}
                allowEditWeekOtherAmount={true}
                draftWeekOtherAmounts={draftOtherAmounts}
                onWeekOtherAmountChange={(weekRequested, otherAmount) => setDraftOtherAmounts(prev => ({ ...prev, [weekRequested]: otherAmount }))}
            />

            <Modal backdrop='static' show={confirm.show} onHide={handleCancelConfirm} className="modal-vertical-center" style={{ display: 'flex', alignItems: 'center' }}>
                <Modal.Header closeButton>
                    {confirm.status == WageAndTravelStatus.PendingWageReimbursement && 'Confirm Wage Reimbursement'}
                    {confirm.status == WageAndTravelStatus.Donated && 'Confirm Convert to Travel Only'}
                </Modal.Header>
                <Modal.Body>
                    {confirm.status == WageAndTravelStatus.PendingWageReimbursement && (
                        <div className="verify-amount-confirm">
                            <div className="verify-amount-confirm-banner">
                                <Glyphicon glyph="exclamation-sign" />
                                <span>Verify this amount carefully before approving. This action authorizes wage reimbursement.</span>
                            </div>
                            <div className="verify-amount-confirm-summary" role="group" aria-label="Approval details">
                                <div className="verify-amount-confirm-field verify-amount-confirm-field-amount">
                                    <div className="verify-amount-confirm-label">Amount to approve</div>
                                    <div className="verify-amount-confirm-value verify-amount-confirm-value-amount">
                                        {`$${wageModel.finalReimbursementTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                                    </div>
                                </div>
                                <div className="verify-amount-confirm-divider" aria-hidden="true" />
                                <div className="verify-amount-confirm-field verify-amount-confirm-field-approver">
                                    <div className="verify-amount-confirm-label">Approver</div>
                                    <div className="verify-amount-confirm-value verify-amount-confirm-value-approver">
                                        {[context.currentUser?.firstName, context.currentUser?.lastName].filter(Boolean).join(' ') || '—'}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {confirm.status == WageAndTravelStatus.Donated &&
                        <p className='warning-outline text-center' style={{ padding: 15, borderRadius: 4 }}>Are you sure? This will change the donor to expense reimbursement only.</p>
                    }
                </Modal.Body>
                <Modal.Footer className="verify-amount-confirm-footer">
                    <Button onClick={handleCancelConfirm} disabled={isConfirming}>Close</Button>
                    <Button
                        bsStyle={confirm.status == WageAndTravelStatus.PendingWageReimbursement ? 'success' : 'warning'}
                        onClick={handleConfirm}
                        disabled={isConfirming}
                    >
                        {confirm.status == WageAndTravelStatus.PendingWageReimbursement ? 'Approve' : 'Confirm'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}

export default VerifyAmount;